import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import { useForgetPassword } from "@/api/client/auth"
import logo from "@/assets/logo.png"
import { Button } from "@/components/ui/button/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form"
import { Input } from "@/components/ui/input/input"

const passwordIssueSchema = z.object({
  email: z
    .string()
    .min(1, "メールアドレスを入力してください")
    .email("有効なメールアドレスを入力してください"),
})

type PasswordIssueFormData = z.infer<typeof passwordIssueSchema>

function AppHeader() {
  return (
    <div className="w-full max-w-sm mb-10 text-center">
      <img src={logo} alt="エージェントコール" className="w-full mb-4" />
      <p className="text-sm text-slate-500 leading-relaxed">
        複数の物件や部屋を簡単に管理し
        <br />
        賃借人とのコミュニケーションを効率化します。
      </p>
    </div>
  )
}

export function PasswordIssuePage() {
  const [isEmailSent, setIsEmailSent] = useState(false)
  const form = useForm<PasswordIssueFormData>({
    resolver: zodResolver(passwordIssueSchema),
    defaultValues: {
      email: "",
    },
  })

  const { mutateAsync: forgetPassword, isPending } = useForgetPassword({
    onSuccess: () => {
      setIsEmailSent(true)
    },
    onError: () => {
      toast.error("メールの送信に失敗しました")
    },
  })

  const onSubmit = (data: PasswordIssueFormData) => {
    forgetPassword(data)
  }

  if (isEmailSent) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <div className="flex-1 flex flex-col items-center px-4 pb-8">
          <AppHeader />

          <h2 className="text-lg font-bold text-slate-700 mb-10">メール送信完了</h2>

          <div className="w-full max-w-sm space-y-10">
            <div className="text-center space-y-4">
              <p className="text-sm text-slate-600 leading-relaxed">
                パスワード再設定用のメールを送信しました。
                <br />
                メールに記載されたリンクから
                <br />
                パスワードの再設定を行ってください。
              </p>
              <p className="text-xs text-slate-500">
                メールが届かない場合は、迷惑メールフォルダをご確認ください。
              </p>
            </div>

            <Button type="button" className="w-full h-11 font-bold" asChild>
              <Link to="/signin">ログイン画面へ</Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center px-4 pb-8">
        <AppHeader />

        {/* Title */}
        <h2 className="text-lg font-bold text-slate-700 mb-10">パスワード再設定</h2>

        {/* Form */}
        <div className="w-full max-w-sm space-y-10">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              {/* Description */}
              <p className="text-sm text-slate-500">
                登録されているメールアドレスを入力してください。
                <br />
                パスワード再設定用のメールをお送りします。
              </p>

              {/* Email Field */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-slate-600">
                      メールアドレス
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="name@company.jp"
                        type="email"
                        autoComplete="email"
                        className="h-11"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Buttons */}
              <div className="space-y-4 pt-3">
                <Button
                  type="submit"
                  className="w-full h-11 font-bold"
                  disabled={form.formState.isSubmitting}
                  loading={isPending}
                >
                  送信する
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11 font-bold text-primary"
                  disabled={form.formState.isSubmitting || isPending}
                  asChild
                >
                  <Link to="/signin">ログイン画面に戻る</Link>
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  )
}
